import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  AU13_CURRENT_FY,
  type Au13Input,
  au13RulesFor,
  calculateAu13,
  careTypeLabel,
  entitlementHeadline,
  explanationBullets,
  formatAud,
  reclaimExplanation,
} from "@aucash/rules/au13";

import { AppBar, Button, Card, Chip, Eyebrow } from "../../src/components/brand";
import { Checkbox, OptionList, SliderInput } from "../../src/components/controls";
import { SaveCard } from "../../src/components/SaveCard";
import { DisclaimerBlock } from "../../src/disclaimer";
import { API_URL, MARKETING_URL } from "../../src/env";
import { usePromocode } from "../../src/promocode";
import { useAppObserva } from "../../src/observa/use-app-observa";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular, webNoOutline } from "../../src/theme";

type Au13Result = Awaited<ReturnType<typeof trpc.calc.getAu13Assessment.query>>;
type PdfReturn = "ready" | "cancelled" | undefined;

export default function Au13Result(): JSX.Element {
  const router = useRouter();
  const params = useLocalSearchParams<{
    id?: string | string[];
    pdf?: string | string[];
  }>();
  const id = Array.isArray(params.id) ? params.id[0] : params.id;
  const pdfParam = Array.isArray(params.pdf) ? params.pdf[0] : params.pdf;
  const pdfReturn: PdfReturn =
    pdfParam === "ready" || pdfParam === "cancelled" ? pdfParam : undefined;

  const [result, setResult] = useState<Au13Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const observa = useAppObserva();

  useEffect(() => {
    if (id === undefined || id === "") return;
    let active = true;
    setResult(null);
    setError(null);
    trpc.calc.getAu13Assessment
      .query({ id })
      .then((res) => {
        if (!active) return;
        setResult(res);
        void observa?.event({
          eventType: "result.viewed",
          payload: { product: "AU13" },
        });
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Request failed");
      });
    return () => {
      active = false;
    };
  }, [id, observa]);

  if (id === undefined || id === "") {
    return (
      <View style={styles.root}>
        <StatusBar style="dark" />
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>We couldn&apos;t find that result</Text>
          <Text style={styles.muted}>
            The link may be incomplete. Head back and enter your details again.
          </Text>
          <Button label="Back to calculator" onPress={() => router.replace("/au13")} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Childcare · result" onRestart={() => router.replace("/au13")} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <Pressable onPress={() => router.replace("/au13")} accessibilityRole="button">
            <Text style={styles.backLink}>← Edit your details</Text>
          </Pressable>

          {/* §3.2: full disclaimer at the top, before any number. */}
          <DisclaimerBlock product="au13" />

          {error !== null ? (
            <Card>
              <Text style={styles.errorTitle}>Couldn&apos;t reach the calculator</Text>
              <Text style={styles.muted}>{error}</Text>
              <Text style={styles.muted}>
                If you&apos;re running this locally, start the API with{" "}
                <Text style={styles.mono}>bun run dev:api</Text> (:7501).
              </Text>
            </Card>
          ) : result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : result.mode === "reclaim" ? (
            <ReclaimBody result={result} id={id} pdfReturn={pdfReturn} />
          ) : (
            <CurrentBody result={result} id={id} />
          )}
        </View>
      </ScrollView>
    </View>
  );
}

function CurrentBody({ result, id }: { result: Au13Result; id: string }): JSX.Element {
  const { output, input, periodLabel, sources, sourcesVerifiedAt } = result;
  const bullets = explanationBullets(output, input.careType, periodLabel, sourcesVerifiedAt);

  return (
    <>
      <View style={styles.resHead}>
        <Eyebrow>Your subsidy · {periodLabel}</Eyebrow>
        <Chip label="● Child Care Subsidy" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{entitlementHeadline(output)}</Text>
        <Text style={styles.heroBig}>
          {formatAud(output.subsidyPerFortnight)}
          <Text style={styles.heroPer}> /fortnight</Text>
        </Text>
        <View style={styles.statList}>
          <StatRow
            label="Subsidised hours"
            value={`${output.subsidisedHours} of ${output.subsidisedHoursEntitlement}/fortnight`}
          />
          <StatRow label="Subsidy per hour" value={formatAud(output.subsidyPerHour)} />
          <StatRow
            label="Your gap (out of pocket)"
            value={`${formatAud(output.gapPerFortnight)}/fortnight`}
          />
        </View>
      </Card>

      <Section title="Why these numbers">
        {bullets.map((b, i) => (
          <Bullet key={i} text={b} />
        ))}
      </Section>

      {/* §7.3 what-if: drag family income and watch the subsidy recompute live
          off the bundled pure engine. Information only (validation §3). */}
      <WhatIf input={input} careLabel={careTypeLabel(input.careType)} />

      <Sources sources={sources} verifiedAt={sourcesVerifiedAt} note={periodLabel} />

      <SaveCard id={id} />

      <CrossSell />
      <EmailCaptureCard />
    </>
  );
}

function ReclaimBody({
  result,
  id,
  pdfReturn,
}: {
  result: Au13Result;
  id: string;
  pdfReturn: PdfReturn;
}): JSX.Element {
  const { output, periodLabel, fy, sources, sourcesVerifiedAt, reclaim } = result;
  if (reclaim === null) return <CurrentBody result={result} id={id} />;

  const bullets = reclaimExplanation({
    periodKey: reclaim.periodKey,
    periodLabel,
    careDate: reclaim.careDate,
    fy,
    output,
    postCheaperChildCareReform: reclaim.postCheaperChildCareReform,
  });

  return (
    <>
      <View style={styles.resHead}>
        <Eyebrow>Personalised summary</Eyebrow>
        <Chip label="Past year" tone="fy" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>What the rules in force then would have paid</Text>
        <Text style={styles.heroBig}>
          {formatAud(output.subsidyPerFortnight)}
          <Text style={styles.heroPer}> /fortnight</Text>
        </Text>
        <View style={styles.statList}>
          <StatRow label="Care date" value={reclaim.careDate} />
          <StatRow label="Rules applied" value={periodLabel} />
        </View>
      </Card>

      <Section title="What this means">
        {bullets.map((b, i) => (
          <Bullet key={i} text={b} />
        ))}
      </Section>

      <ReclaimTriage id={id} pdfReturn={pdfReturn} />

      <Sources sources={sources} verifiedAt={sourcesVerifiedAt} note={periodLabel} />

      <SaveCard id={id} />

      <CrossSell />
      <EmailCaptureCard />
    </>
  );
}

// Reclaim "are you still in time?" triage. Classifies the situation from the
// user's own description and only offers the paid review-support summary for the
// case that may genuinely be recoverable — routing the others to the free path
// (income under-claims self-correct at balancing; out-of-time/unsure → check
// with Services Australia). Deliberately under-promising: we never assert the
// user IS in time or WILL recover money — that's Services Australia's call. The
// paid summary is backed by a full refund if it leads to no recovery.
type ReclaimErrorType = "income" | "activity" | "unsure";

const TRIAGE: Record<
  ReclaimErrorType,
  { heading: string; body: string; offerPaid: boolean }
> = {
  income: {
    heading: "This often sorts itself out — for free",
    body:
      "If your family income estimate was higher than what you actually earned, Services Australia usually pays the difference automatically when your subsidy is balanced after you lodge your tax return. You may not need anything from us — just lodge on time. If you're unsure, contact Services Australia.",
    offerPaid: false,
  },
  activity: {
    heading: "You may be able to ask for a review",
    body:
      "If your activity hours or days were recorded incorrectly, you may be able to ask Services Australia to review the decision — but there are time limits, and we can't tell from here whether you're still within them. Please confirm with Services Australia first. If it helps, we can prepare a one-page summary of what the rules in force then would have paid, for you to take with you.",
    offerPaid: true,
  },
  unsure: {
    heading: "Best to check with Services Australia",
    body:
      "We can't tell from here whether anything can be recovered, or whether you're still in time — that depends on your record and the relevant deadlines. The free place to start is Services Australia or your details in myGov. The figure above is an estimate only, not a Services Australia assessment.",
    offerPaid: false,
  },
};

function ReclaimTriage({
  id,
  pdfReturn,
}: {
  id: string;
  pdfReturn: PdfReturn;
}): JSX.Element {
  const [errorType, setErrorType] = useState<ReclaimErrorType | null>(null);
  const outcome = errorType !== null ? TRIAGE[errorType] : null;

  return (
    <Section title="Can you still get this back?">
      <Text style={styles.muted}>
        Whether a past Child Care Subsidy under-claim can be recovered — and
        whether you're still in time — depends on what happened and on Services
        Australia's deadlines. Tell us what you think went wrong and we'll point
        you to the right (usually free) next step.
      </Text>
      <OptionList<ReclaimErrorType>
        options={[
          {
            value: "income",
            label: "My income estimate was too high",
            sub: "I told them I'd earn more than I actually did",
          },
          {
            value: "activity",
            label: "My activity hours or days were wrong",
            sub: "The hours/days that set my subsidy weren't right",
          },
          { value: "unsure", label: "I'm not sure", sub: "Something else, or I don't know" },
        ]}
        value={errorType}
        onChange={setErrorType}
        testIDPrefix="reclaim-cause"
      />
      {outcome !== null ? (
        <Card tone="sand">
          <Text style={styles.cardTitle}>{outcome.heading}</Text>
          <Text style={styles.muted}>{outcome.body}</Text>
        </Card>
      ) : null}
      {outcome?.offerPaid ? <PaidReclaimCard id={id} pdfReturn={pdfReturn} /> : null}
    </Section>
  );
}

// The ONE paid artifact: the A$10 Child Care Subsidy review-support summary.
// Only rendered when the triage points to a potentially-recoverable case. Copy
// is maximally conservative — it is NOT a Services Australia approval and NOT a
// guarantee of any payment, and it's backed by a full refund if it leads to no
// recovery (validation §3; ACL). A valid beta promocode comps the price; the
// API settles inline (see payments.createPdfCheckout).
function PaidReclaimCard({
  id,
  pdfReturn,
}: {
  id: string;
  pdfReturn: PdfReturn;
}): JSX.Element {
  const promocode = usePromocode();
  const downloadUrl = `${API_URL}/pdf/au13/${id}`;
  const [priceCents, setPriceCents] = useState(1000);
  const [promoApplied, setPromoApplied] = useState(false);
  const [unlocked, setUnlocked] = useState(pdfReturn === "ready");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (promocode === null) return;
    let active = true;
    trpc.payments.checkPromocode
      .query({ promocode, assessmentId: id })
      .then((res) => {
        if (!active) return;
        setPriceCents(res.pdfPriceCents);
        setPromoApplied(res.valid);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [promocode, id]);

  useEffect(() => {
    if (pdfReturn === "ready") void Linking.openURL(downloadUrl);
  }, [pdfReturn, downloadUrl]);

  const onGet = (): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    trpc.payments.createPdfCheckout
      .mutate({ assessmentId: id, promocode: promocode ?? undefined })
      .then((res) => {
        if (res.status === "redirect") {
          void Linking.openURL(res.url);
          return;
        }
        setUnlocked(true);
        void Linking.openURL(downloadUrl);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Couldn't open the summary."),
      )
      .finally(() => setBusy(false));
  };

  if (unlocked) {
    return (
      <Card tone="sand">
        <Text style={styles.cardTitle}>Your summary is ready</Text>
        <Text style={styles.muted}>
          A one-page summary of what the rules in force on your care date would
          have paid, to take to Services Australia. It is not a Services
          Australia approval and not a guarantee of any payment.
        </Text>
        <Button
          label="Open the summary"
          testID="pdf-open"
          onPress={() => void Linking.openURL(downloadUrl)}
        />
      </Card>
    );
  }

  const free = priceCents === 0;
  return (
    <Card tone="sand">
      <Text style={styles.cardTitle}>
        {free ? "Review-support summary — free" : "Review-support summary — A$10"}
      </Text>
      <Text style={styles.muted}>
        A one-page summary of what the rules in force on your care date would have
        paid, that you can take to Services Australia to support a review request.
        It is <Text style={styles.semibold}>not</Text> a Services Australia
        approval, and it is <Text style={styles.semibold}>not</Text> a guarantee
        that you'll recover anything.
      </Text>
      <Text style={styles.refundNote}>
        If it leads to no recovery — or you're simply not happy — we'll refund you
        in full. See our refund policy.
      </Text>
      {promoApplied ? (
        <Text style={styles.promoNote}>Promocode applied — your summary is free.</Text>
      ) : null}
      {pdfReturn === "cancelled" ? (
        <Text style={styles.muted}>No charge — you can come back any time.</Text>
      ) : null}
      <Button
        label={busy ? "Opening…" : free ? "Get the summary — free" : "Get the summary — A$10"}
        testID="pdf-get"
        onPress={onGet}
      />
      {error !== null ? <Text style={styles.muted}>{error}</Text> : null}
    </Card>
  );
}

function WhatIf({ input, careLabel }: { input: Au13Input; careLabel: string }): JSX.Element {
  const rules = useMemo(() => au13RulesFor(AU13_CURRENT_FY), []);
  const [income, setIncome] = useState(input.familyIncome);

  const max = Math.max(250_000, Math.ceil((input.familyIncome * 1.5) / 10_000) * 10_000);

  const out = useMemo(
    () => calculateAu13({ ...input, familyIncome: income }, rules),
    [input, income, rules],
  );

  return (
    <Section title="What if your income changes?">
      <Card tone="sand" style={{ gap: space.md }}>
        <Text style={styles.cellKey}>Combined family income</Text>
        <Text style={styles.whatIfIncome}>{formatAud(income)}</Text>
        <SliderInput
          value={income}
          onChange={setIncome}
          min={0}
          max={max}
          step={1_000}
          maxLabel={`${formatAud(max)}+`}
          hideValue
          accessibilityLabel="Family income to model"
        />
        <View style={styles.whatIfGrid}>
          <View style={styles.whatIfStat}>
            <Text style={styles.cellKey}>Subsidy / fortnight</Text>
            <Text style={styles.whatIfStatValue}>{formatAud(out.subsidyPerFortnight)}</Text>
          </View>
          <View style={styles.whatIfStat}>
            <Text style={styles.cellKey}>Applicable percentage</Text>
            <Text style={styles.whatIfStatValue}>
              {`${+(out.subsidyPercent * 100).toFixed(2)}%`}
            </Text>
          </View>
        </View>
        <Text style={styles.muted}>
          {careLabel} · {out.subsidisedHours} subsidised hours/fortnight
          {out.higherRateApplied ? " · higher second-child rate" : ""}
        </Text>
      </Card>
    </Section>
  );
}

function EmailCaptureCard(): JSX.Element {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <Card tone="sand">
        <Text style={styles.cardTitle}>You&apos;re on the list</Text>
        <Text style={styles.muted}>
          We&apos;ll email you if the thresholds change. Check your inbox for a
          welcome note — every email has a one-tap unsubscribe.
        </Text>
      </Card>
    );
  }

  const canSubmit = consent && email.trim().length > 0 && !submitting;

  const onSubmit = (): void => {
    if (!canSubmit) return;
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    setError(null);
    trpc.email.subscribe
      .mutate({ email: trimmed, consent: true, source: "au13-result" })
      .then(() => setDone(true))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Couldn't sign you up."),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <Card tone="sand">
      <Text style={styles.cardTitle}>Keep these numbers current</Text>
      <Text style={styles.muted}>
        CCS rates, thresholds and rate caps change every July. Get a short email
        when they do — no spam, just the update. We store only your email.
      </Text>
      <TextInput
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        autoCorrect={false}
        keyboardType="email-address"
        textContentType="emailAddress"
        placeholder="you@example.com"
        placeholderTextColor={colors.muted}
        style={styles.input}
        accessibilityLabel="Email address for updates"
      />
      <Checkbox
        checked={consent}
        onChange={setConsent}
        label="Email me occasional updates from Money Back Australia. I can unsubscribe any time."
      />
      <Button
        label={submitting ? "Signing you up…" : "Email me updates"}
        onPress={onSubmit}
        disabled={!canSubmit}
      />
      {error !== null ? <Text style={styles.muted}>{error}</Text> : null}
    </Card>
  );
}

function CrossSell(): JSX.Element {
  const router = useRouter();
  return (
    <Section title="Also worth checking">
      <Pressable onPress={() => router.push("/au08")} accessibilityRole="button" style={styles.crossSell}>
        <Text style={styles.crossSellText}>
          Private health cover? Check your Medicare Levy Surcharge and LHC loading →
        </Text>
        <View style={styles.pill}>
          <Text style={styles.pillText}>Calculate →</Text>
        </View>
      </Pressable>
      <Pressable onPress={() => router.push("/au06")} accessibilityRole="button" style={styles.crossSell}>
        <Text style={styles.crossSellText}>Got HECS debt? Check the marginal repayment for FY26 →</Text>
        <View style={styles.pill}>
          <Text style={styles.pillText}>Calculate →</Text>
        </View>
      </Pressable>
    </Section>
  );
}

function Sources({
  sources,
  verifiedAt,
  note,
}: {
  sources: Record<string, string>;
  verifiedAt: string;
  note: string;
}): JSX.Element {
  return (
    <Section title="Where these numbers come from">
      <SourceLink label="Applicable percentage — DSS Family Assistance Guide" url={sources.percentage} />
      <SourceLink label="Income thresholds — DSS Family Assistance Guide" url={sources.incomeThresholds} />
      <SourceLink label="Hourly rate caps — DSS Family Assistance Guide" url={sources.rateCaps} />
      <SourceLink label="Activity level & subsidised hours — Services Australia" url={sources.hours} />
      <Text style={styles.verified}>
        {note} · rules last verified {verifiedAt}
      </Text>
    </Section>
  );
}

function StatRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <View style={styles.statRow}>
      <Text style={styles.statRowLabel}>{label}</Text>
      <Text style={styles.statRowValue}>{value}</Text>
    </View>
  );
}

function Bullet({ text }: { text: string }): JSX.Element {
  return (
    <View style={styles.whyRow}>
      <View style={styles.whyIcon}>
        <Text style={styles.whyIconText}>i</Text>
      </View>
      <Text style={styles.whyText}>{text}</Text>
    </View>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({ label, url }: { label: string; url: string | undefined }): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 680, gap: space.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", gap: space.md, padding: space.xl },
  loading: { paddingVertical: space.xxl, alignItems: "center" },
  backLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },

  resHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  lead: { fontFamily: fonts.bodyMedium, fontSize: 16, lineHeight: 22, color: colors.inkSoft },
  heroBig: {
    fontFamily: fonts.displayHeavy,
    fontSize: 48,
    letterSpacing: -1.5,
    lineHeight: 52,
    color: colors.honeyDeep,
    marginTop: space.xs,
    ...tabular,
  },
  heroPer: { fontFamily: fonts.bodySemibold, fontSize: 18, color: colors.muted },

  statList: { marginTop: space.md, gap: 0 },
  statRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: space.md,
    paddingVertical: space.sm,
    borderTopWidth: 1,
    borderTopColor: colors.line,
  },
  statRowLabel: { fontFamily: fonts.body, fontSize: 13, color: colors.inkSoft },
  statRowValue: {
    fontFamily: fonts.bodyBold,
    fontSize: 14,
    color: colors.ink,
    flexShrink: 1,
    textAlign: "right",
  },

  section: { gap: space.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 20, letterSpacing: -0.4, color: colors.ink },

  whyRow: { flexDirection: "row", gap: space.md, alignItems: "flex-start" },
  whyIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: colors.honeyTint,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  whyIconText: { fontFamily: fonts.displayHeavy, fontSize: 13, color: colors.honeyDeep },
  whyText: { flex: 1, fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  cellKey: {
    fontFamily: fonts.mono,
    fontSize: 10.5,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.muted,
  },
  whatIfIncome: { fontFamily: fonts.displayHeavy, fontSize: 30, color: colors.ink, ...tabular },
  whatIfGrid: { flexDirection: "row", gap: space.md },
  whatIfStat: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
    gap: space.xs,
  },
  whatIfStatValue: { fontFamily: fonts.displayHeavy, fontSize: 18, color: colors.ink },

  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },

  crossSell: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.md,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
    backgroundColor: colors.card,
  },
  crossSellText: { flex: 1, fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.ink },
  pill: { backgroundColor: colors.honey, borderRadius: radius.sm, paddingHorizontal: space.sm, paddingVertical: space.xs },
  pillText: { fontFamily: fonts.bodyBold, fontSize: 11, color: colors.ink },

  cardTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.ink },
  semibold: { fontFamily: fonts.bodySemibold, color: colors.ink },
  promoNote: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.ocean },
  refundNote: { fontFamily: fonts.body, fontSize: 12.5, lineHeight: 18, color: colors.muted },
  input: {
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingVertical: 14,
    paddingHorizontal: space.lg,
    fontFamily: fonts.bodyMedium,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.card,
    ...webNoOutline,
  },

  errorTitle: { fontFamily: fonts.display, fontSize: 18, color: colors.danger },
  muted: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  mono: { fontFamily: fonts.mono },
});
